/**
 * Category Editor Modal
 * 
 * Wizard-style modal for creating and editing product categories.
 * Supports configuration of costs, seasonal rules, tag mappings, and shipping constraints.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, InputGroup, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Available icons for categories
const AVAILABLE_ICONS = [
    { value: 'box', label: 'Box' },
    { value: 'snowflake', label: 'Snowflake' },
    { value: 'temperature-low', label: 'Cold' },
    { value: 'fire', label: 'Hot' },
    { value: 'leaf', label: 'Organic' },
    { value: 'wine-bottle', label: 'Fragile' },
    { value: 'skull-crossbones', label: 'Hazmat' },
    { value: 'gem', label: 'Luxury' },
    { value: 'cookie', label: 'Food' },
    { value: 'pills', label: 'Medical' },
    { value: 'tag', label: 'Tag' },
    { value: 'star', label: 'Special' }
];

// Color palette
const COLOR_PALETTE = [
    '#6c757d', // Gray
    '#007bff', // Blue
    '#17a2b8', // Cyan
    '#28a745', // Green
    '#ffc107', // Yellow
    '#fd7e14', // Orange
    '#dc3545', // Red
    '#6f42c1', // Purple
    '#e83e8c', // Pink
    '#20c997'  // Teal
];

// Shipping service levels
const SERVICE_LEVELS = [
    { value: '', label: 'No requirement' },
    { value: 'GROUND', label: 'Ground (3-7 days)' },
    { value: '3DAY', label: '3-Day' },
    { value: '2DAY', label: '2-Day' },
    { value: 'NEXTDAY', label: 'Next Day' }
];

// Month options
const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

const DEFAULT_CATEGORY = {
    name: '',
    slug: '',
    color: '#007bff',
    icon: 'tag',
    materialCost: 0,
    handlingCost: 0,
    seasonal: {
        enabled: false,
        startMonth: 5,
        endMonth: 9,
        offSeasonCost: 0
    },
    tagRules: [],
    shippingConstraints: {
        minServiceLevel: ''
    },
    priority: 5
};

export default function CategoryEditorModal({ show, onHide, category, onSave }) {
    const [formData, setFormData] = useState(DEFAULT_CATEGORY);
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [newTagId, setNewTagId] = useState('');
    const [newTagName, setNewTagName] = useState('');

    const isEditing = !!category;
    const totalSteps = 4;

    // Reset form when modal opens/closes or category changes
    useEffect(() => {
        if (show) {
            if (category) {
                setFormData({
                    ...DEFAULT_CATEGORY,
                    ...category,
                    seasonal: { ...DEFAULT_CATEGORY.seasonal, ...category.seasonal },
                    shippingConstraints: { ...DEFAULT_CATEGORY.shippingConstraints, ...category.shippingConstraints }
                });
            } else {
                setFormData(DEFAULT_CATEGORY);
            }
            setStep(1);
            setError(null);
        }
    }, [show, category]);

    // Update form field
    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Update nested field
    const updateNestedField = (parent, field, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [field]: value }
        }));
    };

    // Add tag rule
    const addTagRule = () => {
        if (!newTagId) return;

        const tagId = parseInt(newTagId, 10);
        if (isNaN(tagId)) {
            setError('Tag ID must be a number');
            return;
        }

        // Check for duplicate
        if (formData.tagRules.some(rule => rule.tagId === tagId)) {
            setError('This tag ID is already added');
            return;
        }

        setFormData(prev => ({
            ...prev,
            tagRules: [...prev.tagRules, { tagId, tagName: newTagName || `Tag ${tagId}` }]
        }));
        setNewTagId('');
        setNewTagName('');
        setError(null);
    };

    // Remove tag rule
    const removeTagRule = (tagId) => {
        setFormData(prev => ({
            ...prev,
            tagRules: prev.tagRules.filter(rule => rule.tagId !== tagId)
        }));
    };

    // Generate slug from name
    const generateSlug = (name) => {
        return name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    };

    // Handle name change and auto-generate slug
    const handleNameChange = (name) => {
        updateField('name', name);
        if (!isEditing && !formData.slug) {
            updateField('slug', generateSlug(name));
        }
    };

    // Validate current step
    const validateStep = () => {
        switch (step) {
            case 1:
                if (!formData.name.trim()) {
                    setError('Category name is required');
                    return false;
                }
                break;
            case 2:
                if (formData.materialCost < 0 || formData.handlingCost < 0) {
                    setError('Costs cannot be negative');
                    return false;
                }
                break;
            default:
                break;
        }
        setError(null);
        return true;
    };

    // Navigate to next step
    const nextStep = () => {
        if (validateStep()) {
            setStep(prev => Math.min(prev + 1, totalSteps));
        }
    };

    // Navigate to previous step
    const prevStep = () => {
        setError(null);
        setStep(prev => Math.max(prev - 1, 1));
    };

    // Handle save
    const handleSave = async () => {
        if (!validateStep()) return;

        try {
            setSaving(true);
            setError(null);
            await onSave(formData);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Render step content
    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <div>
                        <h5 className="mb-4">
                            <FontAwesomeIcon icon="info-circle" className="me-2 text-primary" />
                            Basic Information
                        </h5>

                        <Form.Group className="mb-3">
                            <Form.Label>Category Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleNameChange(e.target.value)}
                                placeholder="e.g., Frozen (Dry Ice)"
                                autoFocus
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Slug (Internal ID)</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.slug}
                                onChange={(e) => updateField('slug', e.target.value.toUpperCase())}
                                placeholder="e.g., FROZEN"
                                disabled={isEditing && category.isSystem}
                            />
                            <Form.Text className="text-muted">
                                Used for internal reference and backward compatibility
                            </Form.Text>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Color</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {COLOR_PALETTE.map((color) => (
                                            <div
                                                key={color}
                                                className={`rounded-circle cursor-pointer ${formData.color === color ? 'border border-3 border-dark' : ''}`}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    backgroundColor: color,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => updateField('color', color)}
                                            />
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {AVAILABLE_ICONS.map((icon) => (
                                            <div
                                                key={icon.value}
                                                className={`p-2 rounded cursor-pointer ${formData.icon === icon.value ? 'bg-primary text-white' : 'bg-light'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => updateField('icon', icon.value)}
                                                title={icon.label}
                                            >
                                                <FontAwesomeIcon icon={icon.value} />
                                            </div>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Priority</Form.Label>
                            <Form.Range
                                value={formData.priority}
                                onChange={(e) => updateField('priority', parseInt(e.target.value, 10))}
                                min={0}
                                max={20}
                            />
                            <Form.Text className="text-muted">
                                Higher priority categories take precedence when multiple tags match ({formData.priority})
                            </Form.Text>
                        </Form.Group>
                    </div>
                );

            case 2:
                return (
                    <div>
                        <h5 className="mb-4">
                            <FontAwesomeIcon icon="dollar-sign" className="me-2 text-success" />
                            Cost Configuration
                        </h5>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Material Cost ($)</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.materialCost}
                                            onChange={(e) => updateField('materialCost', parseFloat(e.target.value) || 0)}
                                        />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                        Cost of materials (e.g., cooler, dry ice, ice packs)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Handling Cost ($)</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.handlingCost}
                                            onChange={(e) => updateField('handlingCost', parseFloat(e.target.value) || 0)}
                                        />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                        Additional labor/handling cost
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />

                        <Form.Check
                            type="switch"
                            id="seasonal-switch"
                            label="Enable Seasonal Pricing"
                            checked={formData.seasonal.enabled}
                            onChange={(e) => updateNestedField('seasonal', 'enabled', e.target.checked)}
                            className="mb-3"
                        />

                        {formData.seasonal.enabled && (
                            <div className="bg-light p-3 rounded">
                                <p className="text-muted small mb-3">
                                    <FontAwesomeIcon icon="calendar" className="me-2" />
                                    Apply material costs only during specific months
                                </p>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Start Month</Form.Label>
                                            <Form.Select
                                                value={formData.seasonal.startMonth}
                                                onChange={(e) => updateNestedField('seasonal', 'startMonth', parseInt(e.target.value, 10))}
                                            >
                                                {MONTHS.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>End Month</Form.Label>
                                            <Form.Select
                                                value={formData.seasonal.endMonth}
                                                onChange={(e) => updateNestedField('seasonal', 'endMonth', parseInt(e.target.value, 10))}
                                            >
                                                {MONTHS.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Off-Season Cost ($)</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>$</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.seasonal.offSeasonCost}
                                                    onChange={(e) => updateNestedField('seasonal', 'offSeasonCost', parseFloat(e.target.value) || 0)}
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div>
                        <h5 className="mb-4">
                            <FontAwesomeIcon icon="tags" className="me-2 text-info" />
                            Tag Mapping Rules
                        </h5>

                        <p className="text-muted mb-3">
                            Automatically assign this category to products when their orders have specific ShipStation tags.
                        </p>

                        {/* Existing tags */}
                        {formData.tagRules.length > 0 && (
                            <div className="mb-4">
                                <Form.Label>Current Tag Rules</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {formData.tagRules.map((rule) => (
                                        <Badge
                                            key={rule.tagId}
                                            bg="light"
                                            text="dark"
                                            className="border d-flex align-items-center gap-2 px-3 py-2"
                                        >
                                            <span>{rule.tagName}</span>
                                            <small className="text-muted">(ID: {rule.tagId})</small>
                                            <FontAwesomeIcon
                                                icon="times"
                                                className="text-danger cursor-pointer"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => removeTagRule(rule.tagId)}
                                            />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Add new tag */}
                        <div className="bg-light p-3 rounded">
                            <Form.Label>Add Tag Rule</Form.Label>
                            <Row className="g-2">
                                <Col md={4}>
                                    <Form.Control
                                        type="number"
                                        placeholder="Tag ID (e.g., 76910)"
                                        value={newTagId}
                                        onChange={(e) => setNewTagId(e.target.value)}
                                    />
                                </Col>
                                <Col md={5}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Tag Name (optional)"
                                        value={newTagName}
                                        onChange={(e) => setNewTagName(e.target.value)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Button
                                        variant="outline-primary"
                                        className="w-100"
                                        onClick={addTagRule}
                                        disabled={!newTagId}
                                    >
                                        <FontAwesomeIcon icon="plus" className="me-1" />
                                        Add
                                    </Button>
                                </Col>
                            </Row>
                            <Form.Text className="text-muted">
                                Find tag IDs in ShipStation under Automation → Tags
                            </Form.Text>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div>
                        <h5 className="mb-4">
                            <FontAwesomeIcon icon="shipping-fast" className="me-2 text-warning" />
                            Shipping Constraints
                        </h5>

                        <Form.Group className="mb-4">
                            <Form.Label>Minimum Service Level</Form.Label>
                            <Form.Select
                                value={formData.shippingConstraints.minServiceLevel}
                                onChange={(e) => updateNestedField('shippingConstraints', 'minServiceLevel', e.target.value)}
                            >
                                {SERVICE_LEVELS.map((level) => (
                                    <option key={level.value} value={level.value}>{level.label}</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Products in this category must ship at this speed or faster
                            </Form.Text>
                        </Form.Group>

                        <hr />

                        {/* Preview */}
                        <h5 className="mb-3">Category Preview</h5>
                        <div className="border rounded p-3">
                            <div className="d-flex align-items-center mb-3">
                                <div
                                    className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                                    style={{ backgroundColor: formData.color, width: '48px', height: '48px' }}
                                >
                                    <FontAwesomeIcon icon={formData.icon} className="text-white" size="lg" />
                                </div>
                                <div>
                                    <h5 className="mb-0">{formData.name || 'Untitled Category'}</h5>
                                    <small className="text-muted">{formData.slug || 'NO_SLUG'}</small>
                                </div>
                            </div>

                            <Row className="text-center">
                                <Col>
                                    <div className="text-muted small">Material Cost</div>
                                    <div className="fw-bold">${formData.materialCost.toFixed(2)}</div>
                                </Col>
                                <Col>
                                    <div className="text-muted small">Handling Cost</div>
                                    <div className="fw-bold">${formData.handlingCost.toFixed(2)}</div>
                                </Col>
                                <Col>
                                    <div className="text-muted small">Tag Rules</div>
                                    <div className="fw-bold">{formData.tagRules.length}</div>
                                </Col>
                                <Col>
                                    <div className="text-muted small">Min Ship Level</div>
                                    <div className="fw-bold">{formData.shippingConstraints.minServiceLevel || 'None'}</div>
                                </Col>
                            </Row>

                            {formData.seasonal.enabled && (
                                <div className="mt-3 text-center">
                                    <Badge bg="info">
                                        <FontAwesomeIcon icon="calendar" className="me-1" />
                                        Seasonal: {MONTHS[formData.seasonal.startMonth - 1]?.label} - {MONTHS[formData.seasonal.endMonth - 1]?.label}
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={isEditing ? 'edit' : 'plus'} className="me-2" />
                    {isEditing ? `Edit Category: ${category?.name}` : 'Create New Category'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Step Indicator */}
                <div className="d-flex justify-content-between mb-4">
                    {[
                        { num: 1, label: 'Basics', icon: 'info-circle' },
                        { num: 2, label: 'Costs', icon: 'dollar-sign' },
                        { num: 3, label: 'Tags', icon: 'tags' },
                        { num: 4, label: 'Shipping', icon: 'shipping-fast' }
                    ].map((s) => (
                        <div
                            key={s.num}
                            className={`text-center flex-fill ${step >= s.num ? 'text-primary' : 'text-muted'}`}
                            style={{ cursor: step > s.num ? 'pointer' : 'default' }}
                            onClick={() => step > s.num && setStep(s.num)}
                        >
                            <div
                                className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center ${step === s.num ? 'bg-primary text-white' : step > s.num ? 'bg-success text-white' : 'bg-light'
                                    }`}
                                style={{ width: '36px', height: '36px' }}
                            >
                                {step > s.num ? (
                                    <FontAwesomeIcon icon="check" />
                                ) : (
                                    <FontAwesomeIcon icon={s.icon} />
                                )}
                            </div>
                            <small>{s.label}</small>
                        </div>
                    ))}
                </div>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                        {error}
                    </Alert>
                )}

                {/* Step Content */}
                {renderStepContent()}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>

                {step > 1 && (
                    <Button variant="outline-primary" onClick={prevStep} disabled={saving}>
                        <FontAwesomeIcon icon="arrow-left" className="me-1" />
                        Back
                    </Button>
                )}

                {step < totalSteps ? (
                    <Button variant="primary" onClick={nextStep}>
                        Next
                        <FontAwesomeIcon icon="arrow-right" className="ms-1" />
                    </Button>
                ) : (
                    <Button variant="success" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="check" className="me-1" />
                                {isEditing ? 'Save Changes' : 'Create Category'}
                            </>
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
